"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { DropLocation } from "@/components/waves/drops/Drop";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropActionsOptions from "@/components/waves/drops/WaveDropActionsOptions";
import WaveDropMobileMenuDelete from "@/components/waves/drops/WaveDropMobileMenuDelete";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { MemesArtResubmitAction } from "@/components/waves/memes/submission/MemesArtResubmitAction";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MemeDropTraits from "./MemeDropTraits";
import MemesLeaderboardDropArtistInfo from "./MemesLeaderboardDropArtistInfo";
import MemesLeaderboardDropCard from "./MemesLeaderboardDropCard";
import MemesLeaderboardDropDescription from "./MemesLeaderboardDropDescription";
import MemesLeaderboardDropHeader from "./MemesLeaderboardDropHeader";
import MemesLeaderboardDropVoteSummary from "./MemesLeaderboardDropVoteSummary";
interface MemesLeaderboardDropProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly wave?: ApiWave | undefined;
  readonly location?: DropLocation | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
const getNonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text && text.length > 0 ? text : null;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | null => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export const MemesLeaderboardDrop: React.FC<MemesLeaderboardDropProps> = t0 => {
  const $ = _c(147);
  const {
    drop,
    onDropClick,
    wave,
    location: t1,
    onSourceDropDeleted
  } = t0;
  const location = t1 === undefined ? DropLocation.WAVE : t1;
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    canDelete
  } = useDropInteractionRules(drop);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const openResubmitAfterMenuCloseRef = useRef(false);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let mediaImageScale = ImageScale.AUTOx800;
  if (isMobileScreen) {
    mediaImageScale = ImageScale.AUTOx450;
  } else {
    if (isTabletOrSmaller) {
      mediaImageScale = ImageScale.AUTOx600;
    }
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      openResubmitAfterMenuCloseRef.current = false;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearDeferredResubmitOpen = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      clearDeferredResubmitOpen();
    };
    t4 = [clearDeferredResubmitOpen];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== hasTouchScreen) {
    t5 = {
      hasTouchScreen,
      onInteractionStart: clearDeferredResubmitOpen
    };
    $[3] = hasTouchScreen;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t5);
  let t6;
  if ($[5] !== setIsActive) {
    t6 = () => {
      openResubmitAfterMenuCloseRef.current = true;
      setIsActive(false);
    };
    $[5] = setIsActive;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openResubmitAfterMobileMenuCloses = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!openResubmitAfterMenuCloseRef.current) {
        return;
      }
      clearDeferredResubmitOpen();
      setIsResubmitModalOpen(true);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleMobileMenuAfterLeave = t7;
  let description;
  let t8;
  let title;
  if ($[8] !== drop) {
    const firstPart = drop.parts.at(0);
    title = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    description = getNonEmptyText(firstPart?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    t8 = firstPart?.media.at(0);
    $[8] = drop;
    $[9] = description;
    $[10] = t8;
    $[11] = title;
  } else {
    description = $[9];
    t8 = $[10];
    title = $[11];
  }
  const artworkMedia = t8;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t9;
  if ($[12] !== artworkMedia || $[13] !== canDelete || $[14] !== description || $[15] !== drop || $[16] !== hasTouchScreen || $[17] !== isMobileScreen || $[18] !== location || $[19] !== mediaImageScale || $[20] !== onDropClick || $[21] !== onSourceDropDeleted || $[22] !== title || $[23] !== touchHandlers || $[24] !== wave) {
    const firstThreeVoters = drop.top_raters.slice(0, 3);
    t24 = "tw-w-full tw-cursor-pointer tw-@container";
    if ($[43] !== drop || $[44] !== hasTouchScreen || $[45] !== isMobileScreen || $[46] !== onDropClick) {
      t25 = () => {
        if (hasTouchScreen) {
          return;
        }
        startDropOpen({
          dropId: drop.id,
          waveId: drop.wave.id,
          source: "leaderboard_memes",
          isMobile: isMobileScreen
        });
        onDropClick(drop);
      };
      $[43] = drop;
      $[44] = hasTouchScreen;
      $[45] = isMobileScreen;
      $[46] = onDropClick;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    t23 = "tw-group tw-w-full";
    t22 = touchHandlers;
    T0 = MemesLeaderboardDropCard;
    t21 = drop;
    let t26;
    if ($[48] !== drop) {
      t26 = <MemesLeaderboardDropArtistInfo drop={drop} />;
      $[48] = drop;
      $[49] = t26;
    } else {
      t26 = $[49];
    }
    let t27;
    if ($[50] !== canDelete || $[51] !== drop || $[52] !== hasTouchScreen || $[53] !== onSourceDropDeleted || $[54] !== wave) {
      t27 = !hasTouchScreen && <><WaveDropActionsOpen drop={drop} /><MemesArtResubmitAction drop={drop} wave={wave} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropActionsOptions drop={drop} />}</>;
      $[50] = canDelete;
      $[51] = drop;
      $[52] = hasTouchScreen;
      $[53] = onSourceDropDeleted;
      $[54] = wave;
      $[55] = t27;
    } else {
      t27 = $[55];
    }
    let t28;
    if ($[56] !== t27) {
      t28 = <div className="tw-flex tw-gap-2 tw-text-iron-400">{t27}</div>;
      $[56] = t27;
      $[57] = t28;
    } else {
      t28 = $[57];
    }
    if ($[58] !== t26 || $[59] !== t28) {
      t18 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-start tw-justify-between tw-gap-4">{t26}{t28}</div></div>;
      $[58] = t26;
      $[59] = t28;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    const t29 = artworkMedia?.mime_type;
    let t30;
    if ($[61] !== drop.id || $[62] !== t29) {
      t30 = <MediaTypeBadge mimeType={t29} dropId={drop.id} size="sm" />;
      $[61] = drop.id;
      $[62] = t29;
      $[63] = t30;
    } else {
      t30 = $[63];
    }
    let t31;
    if ($[64] !== title) {
      t31 = <MemesLeaderboardDropHeader title={title} />;
      $[64] = title;
      $[65] = t31;
    } else {
      t31 = $[65];
    }
    let t32;
    if ($[66] !== t30 || $[67] !== t31) {
      t32 = <div className="tw-flex tw-items-center tw-gap-2">{t30}{t31}</div>;
      $[66] = t30;
      $[67] = t31;
      $[68] = t32;
    } else {
      t32 = $[68];
    }
    let t33;
    if ($[69] !== description) {
      t33 = <MemesLeaderboardDropDescription description={description} />;
      $[69] = description;
      $[70] = t33;
    } else {
      t33 = $[70];
    }
    if ($[71] !== t32 || $[72] !== t33) {
      t19 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t32}{t33}</div></div>;
      $[71] = t32;
      $[72] = t33;
      $[73] = t19;
    } else {
      t19 = $[73];
    }
    if ($[74] !== artworkMedia || $[75] !== location || $[76] !== mediaImageScale) {
      t20 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-950" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} imageScale={mediaImageScale} /></div>;
      $[74] = artworkMedia;
      $[75] = location;
      $[76] = mediaImageScale;
      $[77] = t20;
    } else {
      t20 = $[77];
    }
    t16 = "tw-mt-4 tw-flex tw-flex-col tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4";
    if ($[78] !== drop) {
      t17 = <MemeDropTraits drop={drop} />;
      $[78] = drop;
      $[79] = t17;
    } else {
      t17 = $[79];
    }
    t14 = "tw-flex tw-flex-col tw-justify-between tw-gap-4 @[700px]:tw-flex-row @[700px]:tw-items-center";
    if ($[80] !== drop) {
      t15 = <MemesLeaderboardDropVoteSummary drop={drop} />;
      $[80] = drop;
      $[81] = t15;
    } else {
      t15 = $[81];
    }
    t12 = "tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-4 @[700px]:tw-w-auto @[700px]:tw-justify-end";
    t13 = _temp;
    t11 = "tw-flex tw-items-center tw-gap-2 @[700px]:tw-hidden";
    t9 = "tw-flex tw-items-center -tw-space-x-2";
    t10 = firstThreeVoters.map(_temp3);
    $[12] = artworkMedia;
    $[13] = canDelete;
    $[14] = description;
    $[15] = drop;
    $[16] = hasTouchScreen;
    $[17] = isMobileScreen;
    $[18] = location;
    $[19] = mediaImageScale;
    $[20] = onDropClick;
    $[21] = onSourceDropDeleted;
    $[22] = title;
    $[23] = touchHandlers;
    $[24] = wave;
    $[25] = T0;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = t9;
  } else {
    T0 = $[25];
    t10 = $[26];
    t11 = $[27];
    t12 = $[28];
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
    t20 = $[36];
    t21 = $[37];
    t22 = $[38];
    t23 = $[39];
    t24 = $[40];
    t25 = $[41];
    t9 = $[42];
  }
  let t26;
  if ($[82] !== t10 || $[83] !== t9) {
    t26 = <div className={t9}>{t10}</div>;
    $[82] = t10;
    $[83] = t9;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== drop) {
    t27 = <ParticipationDropVoteDetailsTrigger drop={drop} density="compact" />;
    $[85] = drop;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== t11 || $[88] !== t26 || $[89] !== t27) {
    t28 = <div className={t11}>{t26}{t27}</div>;
    $[87] = t11;
    $[88] = t26;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsVotingModalOpen(true);
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== drop) {
    t30 = <VotingModalButton drop={drop} onClick={t29} />;
    $[92] = drop;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t12 || $[95] !== t13 || $[96] !== t28 || $[97] !== t30) {
    t31 = <div className={t12} onClick={t13}>{t28}{t30}</div>;
    $[94] = t12;
    $[95] = t13;
    $[96] = t28;
    $[97] = t30;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  let t32;
  if ($[99] !== t14 || $[100] !== t15 || $[101] !== t31) {
    t32 = <div className={t14}>{t15}{t31}</div>;
    $[99] = t14;
    $[100] = t15;
    $[101] = t31;
    $[102] = t32;
  } else {
    t32 = $[102];
  }
  let t33;
  if ($[103] !== t16 || $[104] !== t17 || $[105] !== t32) {
    t33 = <div className={t16}>{t17}{t32}</div>;
    $[103] = t16;
    $[104] = t17;
    $[105] = t32;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  let t34;
  if ($[107] !== t18 || $[108] !== t19 || $[109] !== t20 || $[110] !== t33) {
    t34 = <div>{t18}{t19}{t20}{t33}</div>;
    $[107] = t18;
    $[108] = t19;
    $[109] = t20;
    $[110] = t33;
    $[111] = t34;
  } else {
    t34 = $[111];
  }
  let t35;
  if ($[112] !== drop || $[113] !== isMobileScreen || $[114] !== isVotingModalOpen) {
    t35 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[112] = drop;
    $[113] = isMobileScreen;
    $[114] = isVotingModalOpen;
    $[115] = t35;
  } else {
    t35 = $[115];
  }
  let t36;
  if ($[116] !== T0 || $[117] !== t21 || $[118] !== t34 || $[119] !== t35) {
    t36 = <T0 drop={t21}>{t34}{t35}</T0>;
    $[116] = T0;
    $[117] = t21;
    $[118] = t34;
    $[119] = t35;
    $[120] = t36;
  } else {
    t36 = $[120];
  }
  let t37;
  if ($[121] !== t22 || $[122] !== t36) {
    t37 = <div {...t22}>{t36}</div>;
    $[121] = t22;
    $[122] = t36;
    $[123] = t37;
  } else {
    t37 = $[123];
  }
  let t38;
  if ($[124] !== canDelete || $[125] !== drop || $[126] !== hasTouchScreen || $[127] !== isActive || $[128] !== onSourceDropDeleted || $[129] !== openResubmitAfterMobileMenuCloses || $[130] !== setIsActive || $[131] !== wave) {
    t38 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive} onAfterLeave={handleMobileMenuAfterLeave}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} /><WaveDropMobileMenuCopyLink drop={drop} onCopy={() => setIsActive(false)} /><MemesArtResubmitAction drop={drop} wave={wave} variant="menu" onOpenModal={openResubmitAfterMobileMenuCloses} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={() => setIsActive(false)} />}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[124] = canDelete;
    $[125] = drop;
    $[126] = hasTouchScreen;
    $[127] = isActive;
    $[128] = onSourceDropDeleted;
    $[129] = openResubmitAfterMobileMenuCloses;
    $[130] = setIsActive;
    $[131] = wave;
    $[132] = t38;
  } else {
    t38 = $[132];
  }
  let t39;
  if ($[133] !== drop || $[134] !== isResubmitModalOpen || $[135] !== onSourceDropDeleted || $[136] !== wave) {
    t39 = wave && isResubmitModalOpen && <MemesArtSubmissionModal isOpen={isResubmitModalOpen} wave={wave} sourceDrop={drop} onClose={() => setIsResubmitModalOpen(false)} onSourceDropDeleted={onSourceDropDeleted} />;
    $[133] = drop;
    $[134] = isResubmitModalOpen;
    $[135] = onSourceDropDeleted;
    $[136] = wave;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  if ($[138] !== t23 || $[139] !== t37 || $[140] !== t38 || $[141] !== t39) {
    t40 = <div className={t23}>{t37}{t38}{t39}</div>;
    $[138] = t23;
    $[139] = t37;
    $[140] = t38;
    $[141] = t39;
    $[142] = t40;
  } else {
    t40 = $[142];
  }
  let t41;
  if ($[143] !== t24 || $[144] !== t25 || $[145] !== t40) {
    t41 = <div className={t24} onClick={t25}>{t40}</div>;
    $[143] = t24;
    $[144] = t25;
    $[145] = t40;
    $[146] = t41;
  } else {
    t41 = $[146];
  }
  return t41;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(voter) {
  return <Link key={voter.profile.handle ?? voter.profile.primary_address} href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp2}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link>;
}